import { useState, useEffect } from 'react';
import { attendanceAPI, employeeAPI, workScheduleAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Calendar as CalendarIcon, CheckCircle2, XCircle, Clock, MapPin, Search, Settings, Users, CalendarDays, Plus, Pencil, Trash2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday } from 'date-fns';
import { cn } from '../../lib/utils';
import ModuleDisabled from '../../components/shared/ModuleDisabled';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "../../components/ui/tooltip";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// ─── Day helpers ─────────────────────────────────────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Convert JS getDay() (0=Sun) → our index (0=Mon)
const jsToOurDay = (jsDay) => (jsDay === 0 ? 6 : jsDay - 1);

function isWorkingSaturday(date, altSatRefDate) {
    if (!altSatRefDate) {
        // Default: 2nd and 4th Saturdays of the month are off; 1st and 3rd are working
        const dayOfMonth = date.getDate();
        const weekOfMonth = Math.ceil(dayOfMonth / 7);
        return weekOfMonth % 2 !== 0; // 1st, 3rd working; 2nd, 4th off
    }
    const ref = new Date(altSatRefDate);
    
    // Timezone-safe week calculation
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const d2 = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
    const diffTime = Math.abs(d1 - d2);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    return diffWeeks % 2 !== 0; // ref is first "off" Saturday; odd weeks from ref = working
}

function isWorkingDay(emp, day, workSchedules) {
    // 1. Check Date of Joining
    if (emp.date_of_joining) {
        const joinDate = new Date(emp.date_of_joining);
        joinDate.setHours(0, 0, 0, 0);
        const compareDay = new Date(day);
        compareDay.setHours(0, 0, 0, 0);
        if (compareDay < joinDate) {
            return false;
        }
    }
    
    // 2. Check Date of Exit / Termination
    if (emp.date_of_exit) {
        const exitDate = new Date(emp.date_of_exit);
        exitDate.setHours(0, 0, 0, 0);
        const compareDay = new Date(day);
        compareDay.setHours(0, 0, 0, 0);
        if (compareDay > exitDate) {
            return false;
        }
    }

    const schedule = emp.work_schedule_id
        ? workSchedules.find(s => s.id === emp.work_schedule_id)
        : workSchedules.find(s => s.is_default);

    if (!schedule) return true;

    const ourDay = jsToOurDay(day.getDay());
    if (!schedule.working_days.includes(ourDay)) return false;

    if (ourDay === 5 && schedule.alternate_saturday) {
        return isWorkingSaturday(day, schedule.alt_sat_ref_date);
    }
    return true;
}

// ─── Schedule type options ─────────────────────────────────────────────────────

const SCHEDULE_TYPES = [
    { value: 'FIVE_DAY', label: '5 Days (Mon–Fri)' },
    { value: 'SIX_DAY', label: '6 Days (Mon–Sat)' },
    { value: 'ALT_SAT', label: 'Alternate Saturdays' },
    { value: 'CUSTOM', label: 'Custom' },
];

const SCHEDULE_TYPE_PRESETS = {
    FIVE_DAY: { working_days: [0, 1, 2, 3, 4], alternate_saturday: false },
    SIX_DAY: { working_days: [0, 1, 2, 3, 4, 5], alternate_saturday: false },
    ALT_SAT: { working_days: [0, 1, 2, 3, 4, 5], alternate_saturday: true },
    CUSTOM: { working_days: [0, 1, 2, 3, 4], alternate_saturday: false },
};

const emptyScheduleForm = {
    name: '',
    description: '',
    schedule_type: 'FIVE_DAY',
    working_days: [0, 1, 2, 3, 4],
    alternate_saturday: false,
    alt_sat_ref_date: '',
    is_default: false,
};

// ─── Work Schedules Panel ─────────────────────────────────────────────────────

const WorkSchedulesPanel = ({ schedules, onRefresh }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyScheduleForm);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyScheduleForm);
        setDialogOpen(true);
    };

    const openEdit = (s) => {
        setEditingId(s.id);
        setForm({
            name: s.name,
            description: s.description || '',
            schedule_type: s.schedule_type,
            working_days: s.working_days,
            alternate_saturday: s.alternate_saturday,
            alt_sat_ref_date: s.alt_sat_ref_date || '',
            is_default: s.is_default,
        });
        setDialogOpen(true);
    };

    const handleTypeChange = (type) => {
        const preset = SCHEDULE_TYPE_PRESETS[type];
        setForm(f => ({ ...f, schedule_type: type, ...preset }));
    };

    const toggleDay = (idx) => {
        setForm(f => {
            const days = f.working_days.includes(idx)
                ? f.working_days.filter(d => d !== idx)
                : [...f.working_days, idx].sort((a, b) => a - b);
            return { ...f, working_days: days };
        });
    };

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Schedule name is required'); return; }
        if (form.working_days.length === 0) { toast.error('Select at least one working day'); return; }

        setSaving(true);
        try {
            const payload = {
                ...form,
                alt_sat_ref_date: form.alt_sat_ref_date || null,
            };
            if (editingId) {
                await workScheduleAPI.update(editingId, payload);
                toast.success('Schedule updated');
            } else {
                await workScheduleAPI.create(payload);
                toast.success('Schedule created');
            }
            setDialogOpen(false);
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save schedule');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await workScheduleAPI.delete(deleteTarget.id);
            toast.success('Schedule deleted');
            setDeleteTarget(null);
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete schedule');
        }
    };

    return (
        <>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <CalendarDays className="h-5 w-5" />
                            Work Schedule Templates
                        </CardTitle>
                        <CardDescription>
                            Define working day patterns. Assign templates to employees in their profile.
                        </CardDescription>
                    </div>
                    <Button onClick={openCreate} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New Schedule
                    </Button>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Working Days</TableHead>
                                <TableHead>Default</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {schedules.map(s => (
                                <TableRow key={s.id}>
                                    <TableCell>
                                        <div>
                                            <p className="font-medium">{s.name}</p>
                                            {s.description && (
                                                <p className="text-xs text-muted-foreground">{s.description}</p>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">
                                            {SCHEDULE_TYPES.find(t => t.value === s.schedule_type)?.label || s.schedule_type}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-1 flex-wrap">
                                            {DAY_LABELS.map((label, idx) => {
                                                const isWorking = s.working_days.includes(idx);
                                                const isAlt = s.alternate_saturday && idx === 5;
                                                return (
                                                    <span
                                                        key={idx}
                                                        className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                                            isWorking
                                                                ? isAlt
                                                                    ? 'bg-amber-100 text-amber-800'
                                                                    : 'bg-green-100 text-green-800'
                                                                : 'bg-muted text-muted-foreground'
                                                        }`}
                                                    >
                                                        {label}{isAlt ? '*' : ''}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        {s.alternate_saturday && (
                                            <p className="text-xs text-amber-700 mt-0.5">* Alternate Saturdays</p>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {s.is_default && (
                                            <Badge className="bg-blue-100 text-blue-800">Default</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button size="icon" variant="ghost" onClick={() => openEdit(s)}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            {!s.is_system && (
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="text-destructive hover:text-destructive"
                                                    onClick={() => setDeleteTarget(s)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Edit Work Schedule' : 'Create Work Schedule'}</DialogTitle>
                        <DialogDescription>
                            Define which days are working days for this schedule template.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Schedule Name *</Label>
                            <Input
                                value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Corporate 5-Day"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Optional description"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Schedule Type</Label>
                            <Select value={form.schedule_type} onValueChange={handleTypeChange}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SCHEDULE_TYPES.map(t => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Working Days</Label>
                            <div className="flex gap-2 flex-wrap">
                                {DAY_LABELS.map((label, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => toggleDay(idx)}
                                        className={cn(
                                            "px-3 py-1.5 rounded-md text-sm font-medium border transition-colors",
                                            form.working_days.includes(idx)
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-background text-muted-foreground border-border hover:bg-muted"
                                        )}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {form.working_days.includes(5) && (
                            <div className="flex items-center justify-between p-3 border rounded-lg">
                                <div>
                                    <p className="font-medium text-sm">Alternate Saturdays</p>
                                    <p className="text-xs text-muted-foreground">
                                        2nd and 4th Saturdays are non-working by default
                                    </p>
                                </div>
                                <Switch
                                    checked={form.alternate_saturday}
                                    onCheckedChange={v => setForm(f => ({ ...f, alternate_saturday: v }))}
                                />
                            </div>
                        )}

                        {form.alternate_saturday && (
                            <div className="space-y-2">
                                <Label>Reference Date (First Off-Saturday)</Label>
                                <Input
                                    type="date"
                                    value={form.alt_sat_ref_date}
                                    onChange={e => setForm(f => ({ ...f, alt_sat_ref_date: e.target.value }))}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Optional. Pin the exact Saturday that starts the off-week cycle.
                                    If empty, the 2nd and 4th Saturdays of each calendar month are off.
                                </p>
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="is_default"
                                checked={form.is_default}
                                onCheckedChange={v => setForm(f => ({ ...f, is_default: !!v }))}
                            />
                            <Label htmlFor="is_default" className="cursor-pointer">
                                Set as company default schedule
                            </Label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editingId ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteTarget?.name}"? Employees assigned to this schedule
                            must be reassigned first.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const AttendanceManagement = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [records, setRecords] = useState([]);
    const [requests, setRequests] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [config, setConfig] = useState({});
    const [workSchedules, setWorkSchedules] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedRequests, setSelectedRequests] = useState(new Set());
    const [confirmAction, setConfirmAction] = useState({ open: false, type: null });
    const [locationNames, setLocationNames] = useState({});

    useEffect(() => {
        fetchData();
    }, [selectedMonth]);

    useEffect(() => {
        const todayRecords = records.filter(r => isToday(new Date(r.date)));
        const toFetch = new Map();
        todayRecords.forEach(r => {
            if (r.check_in_location?.latitude != null && r.check_in_location?.longitude != null) {
                const key = `${r.check_in_location.latitude},${r.check_in_location.longitude}`;
                if (!locationNames[key] && !toFetch.has(key)) {
                    toFetch.set(key, { lat: r.check_in_location.latitude, lng: r.check_in_location.longitude });
                }
            }
        });
        if (toFetch.size === 0) return;
        let cancelled = false;
        const fetchAll = async () => {
            const results = {};
            for (const [key, { lat, lng }] of toFetch) {
                if (cancelled) break;
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
                        { headers: { 'Accept-Language': 'en' } }
                    );
                    const data = await res.json();
                    const a = data.address || {};
                    results[key] = a.suburb || a.neighbourhood || a.city_district || a.quarter ||
                        a.village || a.town || a.city || a.county || data.display_name || null;
                } catch {
                    results[key] = null;
                }
                await new Promise(r => setTimeout(r, 250));
            }
            if (!cancelled) setLocationNames(prev => ({ ...prev, ...results }));
        };
        fetchAll();
        return () => { cancelled = true; };
    }, [records]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const start = format(startOfMonth(selectedMonth), 'yyyy-MM-dd');
            const end = format(endOfMonth(selectedMonth), 'yyyy-MM-dd');

            const [recordsRes, requestsRes, configRes, empRes, schedulesRes] = await Promise.all([
                attendanceAPI.getHistory({ start_date: start, end_date: end }),
                attendanceAPI.getRequests(),
                attendanceAPI.getConfig(),
                employeeAPI.getEmployees({ page_size: 1000 }),
                workScheduleAPI.getAll(),
            ]);

            setRecords(recordsRes.data);
            setRequests(requestsRes.data);
            setConfig(configRes.data);
            setEmployees(empRes.data.employees);
            setWorkSchedules(schedulesRes.data);
        } catch (error) {
            if (error.response?.status === 403) {
                setError(error.response.data.detail);
            } else {
                toast.error('Failed to fetch data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id, status, remarks) => {
        try {
            await attendanceAPI.approveRegularization(id, { status, admin_remarks: remarks });
            toast.success(`Request ${status.toLowerCase()} successfully`);
            fetchData();
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    const handleBulkAction = async () => {
        if (selectedRequests.size === 0 || !confirmAction.type) return;
        const status = confirmAction.type;
        try {
            await attendanceAPI.bulkActionRegularization({
                attendance_ids: Array.from(selectedRequests),
                status,
                admin_remarks: `Bulk ${status.toLowerCase()} by admin`,
            });
            toast.success(`Selected requests ${status.toLowerCase()} successfully`);
            setSelectedRequests(new Set());
            setConfirmAction({ open: false, type: null });
            fetchData();
        } catch (error) {
            toast.error('Bulk operation failed');
        }
    };

    const confirmBulkAction = (type) => setConfirmAction({ open: true, type });

    const handleSelectAll = (checked) => {
        if (checked) setSelectedRequests(new Set(requests.map(r => r.id)));
        else setSelectedRequests(new Set());
    };

    const handleSelectOne = (checked, id) => {
        const newSelected = new Set(selectedRequests);
        if (checked) newSelected.add(id);
        else newSelected.delete(id);
        setSelectedRequests(newSelected);
    };

    const handleUpdateConfig = async () => {
        try {
            await attendanceAPI.updateConfig({ ...config, company_id: records[0]?.company_id || '' });
            toast.success('Configuration updated');
        } catch (error) {
            toast.error('Failed to update config');
        }
    };

    const days = eachDayOfInterval({
        start: startOfMonth(selectedMonth),
        end: endOfMonth(selectedMonth),
    });

    const defaultSchedule = workSchedules.find(s => s.is_default);

    // Returns true when the given JS Date is a non-working day according to the default schedule.
    // Falls back to Sat/Sun if no default schedule exists.
    const isNonWorkingHeader = (day) => {
        if (!defaultSchedule) return day.getDay() === 0 || day.getDay() === 6;
        const ourDay = jsToOurDay(day.getDay());
        if (!defaultSchedule.working_days.includes(ourDay)) return true;
        if (ourDay === 5 && defaultSchedule.alternate_saturday) {
            return !isWorkingSaturday(day, defaultSchedule.alt_sat_ref_date);
        }
        return false;
    };

    const filteredEmployees = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getAttendanceForDay = (empId, day) => {
        const dayStr = format(day, 'yyyy-MM-dd');
        return records.find(r => r.employee_id === empId && r.date === dayStr);
    };

    if (loading && records.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    if (error) {
        return <ModuleDisabled moduleName="attendance" />;
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Attendance Management</h1>
                    <p className="text-muted-foreground">Monitor employee attendance, manage requests and configure schedules</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setSelectedMonth(new Date(selectedMonth.setMonth(selectedMonth.getMonth() - 1)))}>
                        Previous
                    </Button>
                    <div className="px-4 py-2 bg-secondary rounded-md font-medium">
                        {format(selectedMonth, 'MMMM yyyy')}
                    </div>
                    <Button variant="outline" onClick={() => setSelectedMonth(new Date(selectedMonth.setMonth(selectedMonth.getMonth() + 1)))}>
                        Next
                    </Button>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Daily Overview</TabsTrigger>
                    <TabsTrigger value="calendar">Calendar View</TabsTrigger>
                    <TabsTrigger value="requests">
                        Regularizations
                        {requests.length > 0 && (
                            <Badge className="ml-2 bg-red-500">{requests.length}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                {/* ── Daily Overview ─────────────────────────────────────────── */}
                <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
                                <Users className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{employees.length}</div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium">Present Today</CardTitle>
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">
                                    {records.filter(r => isToday(new Date(r.date)) && r.check_in).length}
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium">Late Today</CardTitle>
                                <Clock className="h-4 w-4 text-yellow-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">
                                    {records.filter(r => isToday(new Date(r.date)) && r.status === 'LATE').length}
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium">Absent Today</CardTitle>
                                <XCircle className="h-4 w-4 text-red-500" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">
                                    {employees.length - records.filter(r => isToday(new Date(r.date)) && r.check_in).length}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle>Today's Punch Details</CardTitle>
                            <div className="relative mt-2">
                                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search employees..."
                                    className="pl-8"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Employee</TableHead>
                                        <TableHead>Check In</TableHead>
                                        <TableHead>In Status</TableHead>
                                        <TableHead>Check Out</TableHead>
                                        <TableHead>Out Status</TableHead>
                                        <TableHead>IP Address</TableHead>
                                        <TableHead>Location</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredEmployees.map(emp => {
                                        const rec = records.find(r => r.employee_id === emp.id && isToday(new Date(r.date)));
                                        return (
                                            <TableRow key={emp.id}>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                                                        <p className="text-xs text-muted-foreground">{emp.employee_code}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{rec?.check_in ? format(new Date(rec.check_in), 'hh:mm a') : '--'}</TableCell>
                                                <TableCell>
                                                    {rec?.check_in_status ? (
                                                        <span className={cn(
                                                            "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                                                            rec.check_in_status === 'LATE' ? 'bg-red-50 text-red-600 border border-red-100' :
                                                            rec.check_in_status === 'EARLY' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                                                            'bg-green-50 text-green-600 border border-green-100'
                                                        )}>
                                                            {rec.check_in_status.replace('_', ' ')}
                                                        </span>
                                                    ) : '--'}
                                                </TableCell>
                                                <TableCell>{rec?.check_out ? format(new Date(rec.check_out), 'hh:mm a') : '--'}</TableCell>
                                                <TableCell>
                                                    {rec?.check_out_status ? (
                                                        <span className={cn(
                                                            "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                                                            rec.check_out_status === 'EARLY' ? 'bg-orange-50 text-orange-600 border border-orange-100' :
                                                            rec.check_out_status === 'LATE' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                                                            'bg-green-50 text-green-600 border border-green-100'
                                                        )}>
                                                            {rec.check_out_status.replace('_', ' ')}
                                                        </span>
                                                    ) : '--'}
                                                </TableCell>
                                                <TableCell>
                                                    {rec?.check_in_ip ? (
                                                        <div className="flex items-center gap-1">
                                                            <MapPin className="h-3 w-3 text-muted-foreground" />
                                                            <span className="text-xs">{rec.check_in_ip}</span>
                                                        </div>
                                                    ) : '--'}
                                                </TableCell>
                                                <TableCell>
                                                    {rec?.check_in_location ? (() => {
                                                        const key = `${rec.check_in_location.latitude},${rec.check_in_location.longitude}`;
                                                        const name = rec.check_in_location.address || locationNames[key];
                                                        return (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <div className="flex items-center gap-1 cursor-default max-w-[140px]">
                                                                        <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
                                                                        <span className="text-xs truncate">
                                                                            {name !== undefined
                                                                                ? (name || `${rec.check_in_location.latitude.toFixed(4)}, ${rec.check_in_location.longitude.toFixed(4)}`)
                                                                                : '…'}
                                                                        </span>
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {name || `${rec.check_in_location.latitude}, ${rec.check_in_location.longitude}`}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        );
                                                    })() : '--'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={rec ? (rec.status === 'PRESENT' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800') : 'bg-gray-100 text-gray-500'}>
                                                        {rec?.status || 'NOT PUNCHED'}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Calendar View ──────────────────────────────────────────── */}
                <TabsContent value="calendar">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <CardTitle>Attendance Grid — {format(selectedMonth, 'MMMM yyyy')}</CardTitle>
                                    <CardDescription>Visual summary of attendance for all employees</CardDescription>
                                </div>
                                <div className="relative w-full md:w-64">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search employees..."
                                        className="pl-8"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="sticky left-0 bg-background min-w-[150px]">Employee</TableHead>
                                        {days.map(day => (
                                            <TableHead key={day.toString()} className={cn(
                                                "text-center p-1 min-w-[35px]",
                                                isNonWorkingHeader(day) && "bg-secondary/50"
                                            )}>
                                                {format(day, 'd')}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredEmployees.map(emp => (
                                        <TableRow key={emp.id}>
                                            <TableCell className="sticky left-0 bg-background font-medium text-sm border-r">
                                                {emp.first_name} {emp.last_name}
                                            </TableCell>
                                            {days.map(day => {
                                                const rec = getAttendanceForDay(emp.id, day);
                                                const working = isWorkingDay(emp, day, workSchedules);
                                                const todayStart = new Date();
                                                todayStart.setHours(0, 0, 0, 0);

                                                let dotClass = "bg-gray-100";
                                                let effectiveStatus = null;

                                                if (rec && rec.status !== 'ABSENT') {
                                                    effectiveStatus = rec.status;
                                                    const isRegularized = rec.regularization?.status === 'APPROVED';

                                                    if (!isRegularized && rec.status === 'PRESENT' && rec.check_in) {
                                                        const checkInTime = new Date(rec.check_in);
                                                        const [shiftH, shiftM] = (config.shift_start || "09:00").split(":").map(Number);
                                                        const grace = config.grace_period_mins || 15;
                                                        const checkInMinutes = checkInTime.getHours() * 60 + checkInTime.getMinutes();
                                                        const thresholdMinutes = shiftH * 60 + shiftM + grace;
                                                        if (checkInMinutes > thresholdMinutes) effectiveStatus = 'LATE';
                                                    }

                                                    if (effectiveStatus === 'PRESENT') dotClass = "bg-green-500";
                                                    else if (effectiveStatus === 'LATE') dotClass = "bg-yellow-500";
                                                    else dotClass = "bg-red-500";
                                                } else {
                                                    if (!working) dotClass = "bg-secondary";
                                                    else if (day < todayStart) dotClass = "bg-red-500";
                                                }

                                                let tooltip = "No Record";
                                                if (rec && rec.status !== 'ABSENT') {
                                                    tooltip = `${effectiveStatus}`;
                                                    if (rec.check_in) tooltip += ` — In: ${format(new Date(rec.check_in), 'hh:mm a')}`;
                                                    if (rec.check_out) tooltip += ` Out: ${format(new Date(rec.check_out), 'hh:mm a')}`;
                                                } else {
                                                    tooltip = working ? (day < todayStart ? "Absent" : "No Record") : "Non-Working";
                                                }

                                                return (
                                                    <TableCell key={day.toString()} className="p-1 text-center">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className={cn("h-4 w-4 rounded-full mx-auto cursor-pointer transition-transform hover:scale-110", dotClass)} />
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>{tooltip}</p></TooltipContent>
                                                        </Tooltip>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="mt-4 flex gap-4 text-xs">
                                <div className="flex items-center gap-1"><div className="h-3 w-3 rounded-full bg-green-500" /> Present</div>
                                <div className="flex items-center gap-1"><div className="h-3 w-3 rounded-full bg-yellow-500" /> Late</div>
                                <div className="flex items-center gap-1"><div className="h-3 w-3 rounded-full bg-red-500" /> Absent</div>
                                <div className="flex items-center gap-1"><div className="h-3 w-3 rounded-full bg-secondary border" /> Non-Working</div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Regularizations ────────────────────────────────────────── */}
                <TabsContent value="requests">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>Regularization Requests</CardTitle>
                                <CardDescription>Pending requests for attendance correction</CardDescription>
                            </div>
                            {selectedRequests.size > 0 && (
                                <div className="flex gap-2">
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => confirmBulkAction('APPROVED')}>
                                        Approve Selected ({selectedRequests.size})
                                    </Button>
                                    <Button size="sm" variant="destructive" onClick={() => confirmBulkAction('REJECTED')}>
                                        Reject Selected ({selectedRequests.size})
                                    </Button>
                                </div>
                            )}
                        </CardHeader>
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[50px]">
                                            <Checkbox
                                                checked={requests.length > 0 && selectedRequests.size === requests.length}
                                                onCheckedChange={handleSelectAll}
                                            />
                                        </TableHead>
                                        <TableHead>Employee</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Original</TableHead>
                                        <TableHead>Requested</TableHead>
                                        <TableHead>Reason</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {requests.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                                                No pending requests
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        requests.map(req => (
                                            <TableRow key={req.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedRequests.has(req.id)}
                                                        onCheckedChange={(checked) => handleSelectOne(checked, req.id)}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-medium">{req.employee_name}</TableCell>
                                                <TableCell>{format(new Date(req.date), 'MMM dd, yyyy')}</TableCell>
                                                <TableCell className="text-xs">
                                                    In: {req.regularization.original_check_in ? format(new Date(req.regularization.original_check_in), 'HH:mm') : '--'}<br />
                                                    Out: {req.regularization.original_check_out ? format(new Date(req.regularization.original_check_out), 'HH:mm') : '--'}
                                                </TableCell>
                                                <TableCell className="text-xs text-blue-600 font-medium">
                                                    In: {req.regularization.requested_check_in ? format(new Date(req.regularization.requested_check_in), 'HH:mm') : '--'}<br />
                                                    Out: {req.regularization.requested_check_out ? format(new Date(req.regularization.requested_check_out), 'HH:mm') : '--'}
                                                </TableCell>
                                                <TableCell className="max-w-[200px] truncate" title={req.regularization.reason}>
                                                    {req.regularization.reason}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-2">
                                                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(req.id, 'APPROVED', 'Approved by Admin')}>
                                                            Approve
                                                        </Button>
                                                        <Button size="sm" variant="destructive" onClick={() => handleApprove(req.id, 'REJECTED', 'Rejected by Admin')}>
                                                            Reject
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ── Settings ───────────────────────────────────────────────── */}
                <TabsContent value="settings" className="space-y-6">
                    <WorkSchedulesPanel schedules={workSchedules} onRefresh={fetchData} />

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Settings className="h-5 w-5" />
                                Attendance Policy
                            </CardTitle>
                            <CardDescription>Company-wide shift timing and remote work settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Shift Start</Label>
                                    <Input
                                        type="time"
                                        value={config.shift_start || "09:00"}
                                        onChange={(e) => setConfig({ ...config, shift_start: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Shift End</Label>
                                    <Input
                                        type="time"
                                        value={config.shift_end || "18:00"}
                                        onChange={(e) => setConfig({ ...config, shift_end: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Daily Working Hours</Label>
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={config.working_hours || 9}
                                        onChange={(e) => setConfig({ ...config, working_hours: parseFloat(e.target.value) })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Grace Period (minutes)</Label>
                                    <Input
                                        type="number"
                                        value={config.grace_period_mins || 15}
                                        onChange={(e) => setConfig({ ...config, grace_period_mins: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Min In-Time</Label>
                                    <Input
                                        type="time"
                                        value={config.min_in_time || "08:00"}
                                        onChange={(e) => setConfig({ ...config, min_in_time: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Max In-Time</Label>
                                    <Input
                                        type="time"
                                        value={config.max_in_time || "11:00"}
                                        onChange={(e) => setConfig({ ...config, max_in_time: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-3 border rounded-lg">
                                <div className="space-y-0.5">
                                    <p className="font-medium text-sm">Flexible Working Hours</p>
                                    <p className="text-xs text-muted-foreground">Bypass shift timings</p>
                                </div>
                                <Switch
                                    checked={config.is_flexible || false}
                                    onCheckedChange={(v) => setConfig({ ...config, is_flexible: v })}
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 border rounded-lg">
                                <div className="space-y-0.5">
                                    <p className="font-medium text-sm">Allow WFH</p>
                                    <p className="text-xs text-muted-foreground">Allow remote punches</p>
                                </div>
                                <Switch
                                    checked={config.allow_wfh || false}
                                    onCheckedChange={(v) => setConfig({ ...config, allow_wfh: v })}
                                />
                            </div>

                            <Button onClick={handleUpdateConfig}>Save Policy</Button>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <AlertDialog open={confirmAction.open} onOpenChange={(open) => setConfirmAction({ ...confirmAction, open })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Bulk Action</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to {confirmAction.type?.toLowerCase()} {selectedRequests.size} selected requests?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleBulkAction}
                            className={confirmAction.type === 'APPROVED' ? 'bg-green-600 hover:bg-green-700' : 'bg-destructive hover:bg-destructive/90'}
                        >
                            Confirm {confirmAction.type === 'APPROVED' ? 'Approve' : 'Reject'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AttendanceManagement;
